// alert-confirm.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AlertInputConfig {
  enabled?: boolean;
  label?: string;
  placeholder?: string;
  value?: string;
  type?: 'text' | 'password' | 'email' | 'number';
  required?: boolean;
}

export interface AlertConfirmOptions {
  title?: string;
  message?: string;
  type?: 'alert' | 'confirm';
  theme?: 'success' | 'danger' | 'warning' | 'info' | 'primary';

  input?: AlertInputConfig;
}

export interface AlertConfirmState {
  visible: boolean;
  title: string;
  message: string;
  type: 'alert' | 'confirm';
  theme: 'success' | 'danger' | 'warning' | 'info' | 'primary';

  input: AlertInputConfig;
}

@Injectable({
  providedIn: 'root'
})
export class AlertConfirmService {

  private modalState = new BehaviorSubject<AlertConfirmState>({
    visible: false,
    title: '',
    message: '',
    type: 'alert',
    theme: 'primary',

    input: {
      enabled: false,
      label: '',
      placeholder: '',
      value: '',
      type: 'text',
      required: false
    }
  });

  modalState$: Observable<AlertConfirmState> =
    this.modalState.asObservable();

  private confirmCallback!: (value?: string) => void;
  private cancelCallback!: () => void;

  show(options: AlertConfirmOptions) {

    this.modalState.next({
      visible: true,
      title: options.title || 'Message',
      message: options.message || '',
      type: options.type || 'alert',
      theme: options.theme || 'primary',

      input: {
        enabled: options.input?.enabled || false,
        label: options.input?.label || '',
        placeholder: options.input?.placeholder || '',
        value: options.input?.value || '',
        type: options.input?.type || 'text',
        required: options.input?.required || false
      }
    });
  }

  alert(
    title: string,
    message: string,
    theme: 'success' | 'danger' | 'warning' | 'info' | 'primary' = 'primary'
  ) {

    this.show({
      title,
      message,
      type: 'alert',
      theme
    });
  }

  confirm(
    title: string,
    message: string,
    onConfirm: (value?: string) => void,
    onCancel?: () => void,
    theme: 'success' | 'danger' | 'warning' | 'info' | 'primary' = 'danger',
    input?: AlertInputConfig
  ) {

    this.confirmCallback = onConfirm;

    this.cancelCallback = onCancel || (() => {});

    this.show({
      title,
      message,
      type: 'confirm',
      theme,
      input
    });
  }

  updateInputValue(value: string) {

    this.modalState.next({
      ...this.modalState.value,

      input: {
        ...this.modalState.value.input,
        value
      }
    });
  }

  confirmAction() {

    const inputValue = this.modalState.value.input.value;

    if (
      this.modalState.value.input.required &&
      !inputValue?.trim()
    ) {
      return;
    }

    if (this.confirmCallback) {
      this.confirmCallback(inputValue);
    }

    this.close();
  }

  cancelAction() {

    if (this.cancelCallback) {
      this.cancelCallback();
    }

    this.close();
  }

  close() {

    this.modalState.next({
      ...this.modalState.value,
      visible: false
    });
  }
}
